import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { PackageOpen, PlayCircle, RefreshCw, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RECIPIENTS = [
  'responsible', 'manager', 'admin',
];

/**
 * Chases borrowed and hired kit home. Separate card from the document
 * expiry scanner because the thing expiring is a loan, not a certificate —
 * and because an unreturned hire item bills every day it sits on site.
 */
const AssetReturnReminders = ({ settings, canEdit, onSave }) => {
  const { t } = useTranslation();
  const cfg = settings?.asset_returns || {};
  const [threshold, setThreshold] = useState('');
  const [scanning, setScanning] = useState(false);

  const patch = (changes) => onSave({ asset_returns: { ...cfg, ...changes } });

  const addThreshold = () => {
    const n = parseInt(threshold, 10);
    if (!n || n < 0 || n > 365) { toast.error(t('inv.returns.daysRange')); return; }
    const days = [...new Set([...(cfg.days_before || []), n])].sort((a, b) => b - a);
    setThreshold('');
    patch({ days_before: days });
  };

  const removeThreshold = (n) =>
    patch({ days_before: (cfg.days_before || []).filter((d) => d !== n) });

  const runScan = async () => {
    setScanning(true);
    try {
      const { data } = await axios.post(`${API}/reminders/scan-asset-returns`);
      if (data.skipped) toast.info(`Scan skipped (${data.skipped})`);
      else toast.success(
        `${data.outstanding} item(s) still out, ${data.overdue} overdue, ${data.reminders_fired} reminder(s) sent`,
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || t('inv.returns.scanFailed'));
    } finally { setScanning(false); }
  };

  return (
    <Card data-testid="asset-return-reminders-card">
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <PackageOpen className="h-4 w-4" /> {t('inv.returns.title')}
          </CardTitle>
          <p className="mt-0.5 text-xs text-slate-500">{t('inv.returns.hint')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={runScan} disabled={scanning}
          data-testid="run-asset-return-scan-btn">
          {scanning
            ? <><RefreshCw className="mr-1 h-3 w-3 animate-spin" />{t('inv.returns.scanning')}</>
            : <><PlayCircle className="mr-1 h-3 w-3" />{t('inv.returns.runNow')}</>}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex cursor-pointer select-none items-start gap-2.5 rounded-lg border p-2.5">
          <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#AA0000]"
            checked={cfg.enabled !== false} disabled={!canEdit}
            onChange={(e) => patch({ enabled: e.target.checked })}
            data-testid="asset-returns-enabled" />
          <span>
            <span className="text-sm font-medium">{t('inv.returns.chaseReturns')}</span>
            <span className="mt-0.5 block text-xs text-slate-500">{t('inv.returns.chaseReturnsHint')}</span>
          </span>
        </label>

        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('inv.returns.daysBefore')}
          </p>
          <div className="flex flex-wrap items-center gap-1.5">
            {(cfg.days_before || []).length === 0 && (
              <span className="text-xs text-slate-400">{t('inv.returns.noAdvance')}</span>
            )}
            {(cfg.days_before || []).map((d) => (
              <Badge key={d} variant="secondary" className="gap-1" data-testid={`asset-return-threshold-${d}`}>
                {t('inv.returns.daysCount', { count: d })}
                {canEdit && (
                  <button onClick={() => removeThreshold(d)} className="text-slate-500 hover:text-red-600"
                    data-testid={`remove-asset-return-threshold-${d}`}>
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
            {canEdit && (
              <span className="flex items-center gap-1">
                <Input value={threshold} onChange={(e) => setThreshold(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addThreshold(); } }}
                  placeholder={t('inv.returns.days')} type="number" className="h-7 w-20 text-xs"
                  data-testid="asset-return-threshold-input" />
                <Button size="sm" variant="outline" className="h-7" onClick={addThreshold}
                  data-testid="add-asset-return-threshold-btn">{t('common.add')}</Button>
              </span>
            )}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            {t('inv.returns.alsoOnDay')}
          </p>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('inv.returns.overdueRepeat')}
          </p>
          <div className="flex items-center gap-2">
            <Input type="number" min="1" max="90" className="h-8 w-24 text-sm"
              value={cfg.overdue_repeat_days ?? 7} disabled={!canEdit}
              onChange={(e) => patch({ overdue_repeat_days: Math.max(1, parseInt(e.target.value, 10) || 7) })}
              data-testid="asset-return-overdue-repeat" />
            <span className="text-sm text-slate-600">{t('inv.returns.days')}</span>
          </div>
        </div>

        <div>
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('inv.returns.whoGetsTold')}</p>
          <div className="space-y-1">
            {RECIPIENTS.map((key) => (
              <label key={key}
                className="flex cursor-pointer select-none items-start gap-2.5 rounded-lg border p-2.5"
                data-testid={`asset-return-recipient-row-${key}`}>
                <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#AA0000]"
                  checked={(cfg.recipients || {})[key] !== false} disabled={!canEdit}
                  onChange={(e) => patch({ recipients: { ...(cfg.recipients || {}), [key]: e.target.checked } })}
                  data-testid={`asset-return-recipient-${key}`} />
                <span>
                  <span className="text-sm font-medium">{t(`inv.returns.recipients.${key}.label`)}</span>
                  <span className="mt-0.5 block text-xs text-slate-500">{t(`inv.returns.recipients.${key}.hint`)}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AssetReturnReminders;
